import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Card, Row, Col, Spinner, Alert, Table, Badge, Form, Button } from "react-bootstrap";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import api from "api";
import "./LogsDashboard.css";
import PageHeader from "components/layout/page-header/PageHeader";
import TrialInsightsPanel from "pages/admin/TrialInsightsPanel";
import UserTimelineModal from "pages/admin/UserTimelineModal";

function scrollToLogsAnchor(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function LogsDashboard({ token, embedded, onSwitchToUsersTab }) {
  const [timelineUser, setTimelineUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [periodDays, setPeriodDays] = useState(7);

  useEffect(() => {
    if (token) {
      fetchDashboardStats();
    }
  }, [token, periodDays]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get("/logs/system-dashboard", {
        headers: { Authorization: `Bearer ${token}` },
        params: { days: periodDays }
      });

      setStats(response.data);
    } catch (error) {
      setError("Erro ao carregar dados do dashboard");
    } finally {
      setLoading(false);
    }
  };

  // Configuração do gráfico de barras para requisições por endpoint
  const getApiRequestsChartData = () => {
    if (!stats?.api_requests?.by_endpoint) {
      return {
        labels: [],
        datasets: []
      };
    }

    const endpoints = stats.api_requests.by_endpoint.slice(0, 10); // Top 10 endpoints

    return {
      labels: endpoints.map(item => {
        // Trunca endpoints muito longos para melhor visualização
        const endpoint = item.endpoint;
        return endpoint.length > 30
          ? endpoint.substring(0, 30) + "..."
          : endpoint;
      }),
      datasets: [
        {
          label: "Requisições",
          backgroundColor: [
            "rgba(3, 81, 132, 0.8)",
            "rgba(26, 107, 184, 0.8)",
            "rgba(40, 167, 69, 0.8)",
            "rgba(23, 162, 184, 0.8)",
            "rgba(255, 193, 7, 0.8)",
            "rgba(220, 53, 69, 0.8)",
            "rgba(108, 117, 125, 0.8)",
            "rgba(111, 66, 193, 0.8)",
            "rgba(253, 126, 20, 0.8)",
            "rgba(102, 16, 242, 0.8)"
          ],
          borderColor: [
            "rgba(3, 81, 132, 1)",
            "rgba(26, 107, 184, 1)",
            "rgba(40, 167, 69, 1)",
            "rgba(23, 162, 184, 1)",
            "rgba(255, 193, 7, 1)",
            "rgba(220, 53, 69, 1)",
            "rgba(108, 117, 125, 1)",
            "rgba(111, 66, 193, 1)",
            "rgba(253, 126, 20, 1)",
            "rgba(102, 16, 242, 1)"
          ],
          borderWidth: 1,
          data: endpoints.map(item => item.count)
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: false
    },
    scales: {
      xAxes: [
        {
          ticks: {
            beginAtZero: true,
            fontColor: "#6c757d",
            fontSize: 10,
            maxRotation: 45,
            minRotation: 45
          },
          gridLines: {
            color: "rgba(0, 0, 0, 0.1)",
            drawBorder: false
          }
        }
      ],
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            fontColor: "#6c757d",
            fontSize: 10
          },
          gridLines: {
            color: "rgba(0, 0, 0, 0.1)",
            drawBorder: false
          }
        }
      ]
    },
    tooltips: {
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      titleFontColor: "#fff",
      bodyFontColor: "#fff",
      borderColor: "#035184",
      borderWidth: 1,
      callbacks: {
        title: function(tooltipItems, data) {
          const index = tooltipItems[0].index;
          const endpoints = stats?.api_requests?.by_endpoint || [];
          if (endpoints[index]) {
            return endpoints[index].endpoint;
          }
          return data.labels[index];
        },
        label: function(tooltipItem, data) {
          return "Requisições: " + tooltipItem.yLabel;
        }
      }
    },
    // Configurações adicionais para garantir que o gráfico seja exibido
    animation: {
      duration: 1000
    },
    layout: {
      padding: {
        top: 20,
        bottom: 20,
        left: 20,
        right: 20
      }
    }
  };

  // Configuração do gráfico de linha para performance ao longo do tempo
  const getPerformanceChartData = () => {
    if (!stats?.performance_trend || stats.performance_trend.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }

    const performanceData = stats.performance_trend;

    return {
      labels: performanceData.map(item => item.hour),
      datasets: [
        {
          label: "Tempo de Resposta (ms)",
          data: performanceData.map(item => item.avg_response_time),
          borderColor: "#035184",
          backgroundColor: "rgba(3, 81, 132, 0.1)",
          borderWidth: 3,
          fill: true,
          pointBackgroundColor: "#035184",
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.4
        }
      ]
    };
  };

  // Função para calcular tempo decorrido desde o início da sessão
  const getSessionDuration = sessionStart => {
    if (!sessionStart) return "N/A";

    const start = new Date(sessionStart);
    const now = new Date();
    const diff = now - start;

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const performanceChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: true,
      position: "top",
      labels: {
        fontColor: "#6c757d",
        fontSize: 12,
        usePointStyle: true
      }
    },
    scales: {
      xAxes: [
        {
          ticks: {
            fontColor: "#6c757d",
            fontSize: 10
          },
          gridLines: {
            color: "rgba(0, 0, 0, 0.1)",
            drawBorder: false
          }
        }
      ],
      yAxes: [
        {
          ticks: {
            fontColor: "#6c757d",
            fontSize: 10,
            callback: function(value) {
              return value + " ms";
            }
          },
          gridLines: {
            color: "rgba(0, 0, 0, 0.1)",
            drawBorder: false
          }
        }
      ]
    },
    tooltips: {
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      titleFontColor: "#fff",
      bodyFontColor: "#fff",
      borderColor: "#035184",
      borderWidth: 1,
      callbacks: {
        title: function(tooltipItems, data) {
          return "Hora: " + data.labels[tooltipItems[0].index];
        },
        label: function(tooltipItem, data) {
          return "Tempo de Resposta: " + tooltipItem.yLabel + " ms";
        }
      }
    },
    animation: {
      duration: 1000
    },
    layout: {
      padding: {
        top: 20,
        bottom: 20,
        left: 20,
        right: 20
      }
    }
  };

  // Configuração do gráfico de pizza para distribuição de status codes
  const getStatusDistributionChartData = () => {
    if (!stats?.status_distribution || stats.status_distribution.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }

    const statusData = stats.status_distribution;

    return {
      labels: statusData.map(item => item.status_group),
      datasets: [
        {
          data: statusData.map(item => item.count),
          backgroundColor: [
            "rgba(40, 167, 69, 0.8)", // Verde para 2xx
            "rgba(23, 162, 184, 0.8)", // Azul para 3xx
            "rgba(255, 193, 7, 0.8)", // Amarelo para 4xx
            "rgba(220, 53, 69, 0.8)" // Vermelho para 5xx
          ],
          borderColor: [
            "rgba(40, 167, 69, 1)",
            "rgba(23, 162, 184, 1)",
            "rgba(255, 193, 7, 1)",
            "rgba(220, 53, 69, 1)"
          ],
          borderWidth: 2,
          hoverBackgroundColor: [
            "rgba(40, 167, 69, 1)",
            "rgba(23, 162, 184, 1)",
            "rgba(255, 193, 7, 1)",
            "rgba(220, 53, 69, 1)"
          ]
        }
      ]
    };
  };

  const statusDistributionChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: true,
      position: "bottom",
      labels: {
        fontColor: "#6c757d",
        fontSize: 12,
        usePointStyle: true,
        padding: 20
      }
    },
    tooltips: {
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      titleFontColor: "#fff",
      bodyFontColor: "#fff",
      borderColor: "#035184",
      borderWidth: 1,
      callbacks: {
        title: function(tooltipItems, data) {
          return "Status Code: " + data.labels[tooltipItems[0].index];
        },
        label: function(tooltipItem, data) {
          const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
          const percentage = ((tooltipItem.yLabel / total) * 100).toFixed(1);
          return (
            "Requisições: " + tooltipItem.yLabel + " (" + percentage + "%)"
          );
        }
      }
    },
    animation: {
      duration: 1000
    },
    layout: {
      padding: {
        top: 20,
        bottom: 20,
        left: 20,
        right: 20
      }
    }
  };

  // Configuração do gráfico de barras para endpoints lentos
  const getSlowEndpointsChartData = () => {
    if (!stats?.slow_endpoints || stats.slow_endpoints.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }

    const slowEndpoints = stats.slow_endpoints.slice(0, 10); // Top 10 endpoints

    return {
      labels: slowEndpoints.map(item => {
        // Trunca endpoints muito longos para melhor visualização
        const endpoint = item.endpoint;
        return endpoint.length > 40
          ? endpoint.substring(0, 40) + "..."
          : endpoint;
      }),
      datasets: [
        {
          label: "Tempo Médio de Resposta (ms)",
          backgroundColor: [
            "rgba(220, 53, 69, 0.8)", // Vermelho para endpoints mais lentos
            "rgba(220, 53, 69, 0.7)",
            "rgba(220, 53, 69, 0.6)",
            "rgba(255, 193, 7, 0.8)", // Amarelo
            "rgba(255, 193, 7, 0.7)",
            "rgba(255, 193, 7, 0.6)",
            "rgba(23, 162, 184, 0.8)", // Azul
            "rgba(23, 162, 184, 0.7)",
            "rgba(23, 162, 184, 0.6)",
            "rgba(40, 167, 69, 0.8)" // Verde para endpoints menos lentos
          ],
          borderColor: [
            "rgba(220, 53, 69, 1)",
            "rgba(220, 53, 69, 1)",
            "rgba(220, 53, 69, 1)",
            "rgba(255, 193, 7, 1)",
            "rgba(255, 193, 7, 1)",
            "rgba(255, 193, 7, 1)",
            "rgba(23, 162, 184, 1)",
            "rgba(23, 162, 184, 1)",
            "rgba(23, 162, 184, 1)",
            "rgba(40, 167, 69, 1)"
          ],
          borderWidth: 1,
          data: slowEndpoints.map(item => item.avg_response_time)
        }
      ]
    };
  };

  const slowEndpointsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: false
    },
    scales: {
      xAxes: [
        {
          ticks: {
            beginAtZero: true,
            fontColor: "#6c757d",
            fontSize: 10,
            maxRotation: 45,
            minRotation: 45
          },
          gridLines: {
            color: "rgba(0, 0, 0, 0.1)",
            drawBorder: false
          }
        }
      ],
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            fontColor: "#6c757d",
            fontSize: 10,
            callback: function(value) {
              return value + " ms";
            }
          },
          gridLines: {
            color: "rgba(0, 0, 0, 0.1)",
            drawBorder: false
          }
        }
      ]
    },
    tooltips: {
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      titleFontColor: "#fff",
      bodyFontColor: "#fff",
      borderColor: "#035184",
      borderWidth: 1,
      callbacks: {
        title: function(tooltipItems, data) {
          const index = tooltipItems[0].index;
          const slowEndpoints = stats?.slow_endpoints || [];
          if (slowEndpoints[index]) {
            return slowEndpoints[index].endpoint;
          }
          return data.labels[index];
        },
        label: function(tooltipItem, data) {
          const index = tooltipItem.index;
          const slowEndpoints = stats?.slow_endpoints || [];
          if (slowEndpoints[index]) {
            return [
              "Tempo Médio: " + tooltipItem.yLabel + " ms",
              "Requisições: " + slowEndpoints[index].request_count
            ];
          }
          return "Tempo Médio: " + tooltipItem.yLabel + " ms";
        }
      }
    },
    animation: {
      duration: 1000
    },
    layout: {
      padding: {
        top: 20,
        bottom: 20,
        left: 20,
        right: 20
      }
    }
  };

  if (!token) {
    return (
      <div className="logs-dashboard">
        {!embedded && (
          <PageHeader
            title="Dashboard de Logs"
            subtitle="Monitoramento do sistema e requisições em tempo quase real."
            accent="Administração"
          />
        )}
        <Alert variant="warning">
          <Alert.Heading>Acesso Negado</Alert.Heading>
          <p>Você precisa estar logado para acessar este dashboard.</p>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="logs-dashboard">
        {!embedded && (
          <PageHeader
            title="Dashboard de Logs"
            subtitle="Monitoramento do sistema e requisições em tempo quase real."
            accent="Administração"
          />
        )}
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner animation="border" variant="primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="logs-dashboard">
        {!embedded && (
          <PageHeader
            title="Dashboard de Logs"
            subtitle="Monitoramento do sistema e requisições em tempo quase real."
            accent="Administração"
          />
        )}
        <Alert variant="danger">
          <Alert.Heading>Erro ao carregar dados</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </div>
    );
  }

  return (
    <div
      className="logs-dashboard"
      style={{ minHeight: "100vh", overflowY: "auto" }}
    >
      {!embedded && (
        <div className="dashboard-header">
          <h2>Dashboard de Logs</h2>
          <small>Monitoramento do sistema em tempo real</small>
        </div>
      )}

      <Row className="mb-3 align-items-center justify-content-between g-2">
        <Col xs="auto" className="d-flex flex-wrap gap-2 align-items-center">
          {embedded && typeof onSwitchToUsersTab === "function" && (
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              className="rounded-pill"
              onClick={onSwitchToUsersTab}
            >
              <i className="fas fa-users me-1" />
              Aba Usuários
            </Button>
          )}
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            className="rounded-pill"
            onClick={() => scrollToLogsAnchor("admin-trial-insights-logs")}
          >
            <i className="fas fa-hourglass-half me-1" />
            Trials
          </Button>
        </Col>
        <Col xs="auto">
          <Form.Select
            value={periodDays}
            onChange={(e) => setPeriodDays(Number(e.target.value))}
            aria-label="Quantidade de dias para logs"
          >
            <option value={7}>Últimos 7 dias</option>
            <option value={14}>Últimos 14 dias</option>
            <option value={30}>Últimos 30 dias</option>
          </Form.Select>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs={12}>
          <div id="admin-trial-insights-logs">
            <TrialInsightsPanel
              token={token}
              variant="panel"
              onUserClick={u =>
                setTimelineUser({ id: u.id, username: u.username || u.email })
              }
            />
          </div>
        </Col>
      </Row>

      <Row className={embedded ? "mt-2" : "mt-6"}>
        {/* Total de Requisições (Hoje) */}
        <Col md={2} className="mb-4">
          <Card
            className="metric-card h-100"
            role="button"
            tabIndex={0}
            style={{ cursor: "pointer" }}
            onClick={() => scrollToLogsAnchor("logs-chart-endpoints")}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                scrollToLogsAnchor("logs-chart-endpoints");
              }
            }}
          >
            <Card.Body className="text-center">
              <div className="mb-3">
                <i
                  className="fas fa-chart-line text-primary"
                  style={{ fontSize: "2rem" }}
                ></i>
              </div>
              <h4 className="text-primary mb-2">
                {stats?.overview?.today_requests?.toLocaleString() || "0"}
              </h4>
              <p className="text-muted mb-0">Requisições hoje · clique → gráfico</p>
            </Card.Body>
          </Card>
        </Col>

        {/* Tempo Médio de Resposta */}
        <Col md={2} className="mb-4">
          <Card
            className="metric-card h-100"
            role="button"
            tabIndex={0}
            style={{ cursor: "pointer" }}
            onClick={() => scrollToLogsAnchor("logs-chart-performance")}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                scrollToLogsAnchor("logs-chart-performance");
              }
            }}
          >
            <Card.Body className="text-center">
              <div className="mb-3">
                <i
                  className="fas fa-clock text-info"
                  style={{ fontSize: "2rem" }}
                ></i>
              </div>
              <h4 className="text-info mb-2">
                {stats?.overview?.avg_response_time || "0"} ms
              </h4>
              <p className="text-muted mb-0">Latência · clique → tendência</p>
            </Card.Body>
          </Card>
        </Col>

        {/* Taxa de Erros */}
        <Col md={2} className="mb-4">
          <Card
            className="metric-card h-100"
            role="button"
            tabIndex={0}
            style={{ cursor: "pointer" }}
            onClick={() => scrollToLogsAnchor("logs-chart-status")}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                scrollToLogsAnchor("logs-chart-status");
              }
            }}
          >
            <Card.Body className="text-center">
              <div className="mb-3">
                <i
                  className="fas fa-exclamation-triangle text-warning"
                  style={{ fontSize: "2rem" }}
                ></i>
              </div>
              <h4 className="text-warning mb-2">
                {stats?.overview?.error_rate || "0"}%
              </h4>
              <p className="text-muted mb-0">Erros · clique → status HTTP</p>
            </Card.Body>
          </Card>
        </Col>

        {/* Sessões Ativas */}
        <Col md={2} className="mb-4">
          <Card
            className="metric-card h-100"
            role="button"
            tabIndex={0}
            style={{ cursor: "pointer" }}
            onClick={() => scrollToLogsAnchor("logs-section-sessions")}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                scrollToLogsAnchor("logs-section-sessions");
              }
            }}
          >
            <Card.Body className="text-center">
              <div className="mb-3">
                <i
                  className="fas fa-users text-success"
                  style={{ fontSize: "2rem" }}
                ></i>
              </div>
              <h4 className="text-success mb-2">
                {stats?.overview?.active_sessions || "0"}
              </h4>
              <p className="text-muted mb-0">Sessões · clique → detalhe</p>
            </Card.Body>
          </Card>
        </Col>

        {/* Uso de CPU */}
        <Col md={2} className="mb-4">
          <Card
            className="metric-card h-100"
            role="button"
            tabIndex={0}
            style={{ cursor: "pointer" }}
            onClick={() => scrollToLogsAnchor("admin-trial-insights-logs")}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                scrollToLogsAnchor("admin-trial-insights-logs");
              }
            }}
          >
            <Card.Body className="text-center">
              <div className="mb-3">
                <i
                  className="fas fa-microchip text-danger"
                  style={{ fontSize: "2rem" }}
                ></i>
              </div>
              <h4 className="text-danger mb-2">
                {stats?.overview?.avg_cpu || "0"}%
              </h4>
              <p className="text-muted mb-0">CPU · clique → trials</p>
            </Card.Body>
          </Card>
        </Col>

        {/* Uso de Memória */}
        <Col md={2} className="mb-4">
          <Card
            className="metric-card h-100"
            role="button"
            tabIndex={0}
            style={{ cursor: "pointer" }}
            onClick={() => scrollToLogsAnchor("logs-slow-endpoints")}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                scrollToLogsAnchor("logs-slow-endpoints");
              }
            }}
          >
            <Card.Body className="text-center">
              <div className="mb-3">
                <i
                  className="fas fa-memory text-secondary"
                  style={{ fontSize: "2rem" }}
                ></i>
              </div>
              <h4 className="text-secondary mb-2">
                {stats?.overview?.avg_memory || "0"}%
              </h4>
              <p className="text-muted mb-0">Memória · clique → lentos</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Gráfico de Requisições por Endpoint */}
      <Row className="mt-4" id="logs-chart-endpoints">
        <Col md={12} className="mb-4">
          <Card className="metric-card">
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-chart-bar text-primary me-2"></i>
                Requisições por Endpoint
              </h5>
              <small className="text-muted">
                Top 10 endpoints mais acessados
              </small>
            </Card.Header>
            <Card.Body style={{ padding: "20px" }}>
              {stats?.api_requests?.by_endpoint &&
              stats.api_requests.by_endpoint.length > 0 ? (
                <div
                  className="chart-container"
                  style={{ height: "450px", position: "relative" }}
                >
                  <Bar
                    data={getApiRequestsChartData()}
                    options={chartOptions}
                    height={450}
                    width={null}
                  />
                </div>
              ) : (
                <div
                  style={{
                    height: "450px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <p className="text-muted">
                    Nenhum dado de endpoint disponível
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Gráfico de Performance ao Longo do Tempo */}
      <Row className="mt-4" id="logs-chart-performance">
        <Col md={12} className="mb-4">
          <Card className="metric-card">
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-chart-line text-info me-2"></i>
                Performance ao Longo do Tempo
              </h5>
              <small className="text-muted">
                Evolução do tempo de resposta médio por hora (últimas 24h)
              </small>
            </Card.Header>
            <Card.Body style={{ padding: "20px" }}>
              {stats?.performance_trend &&
              stats.performance_trend.length > 0 ? (
                <div
                  className="line-chart-container"
                  style={{ height: "450px", position: "relative" }}
                >
                  <Line
                    data={getPerformanceChartData()}
                    options={performanceChartOptions}
                    height={450}
                    width={null}
                  />
                </div>
              ) : (
                <div
                  style={{
                    height: "450px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <p className="text-muted">
                    Nenhum dado de performance disponível
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Gráfico de Distribuição de Status Codes */}
      <Row className="mt-4" id="logs-chart-status">
        <Col md={6} className="mb-4">
          <Card className="metric-card">
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-chart-pie text-warning me-2"></i>
                Distribuição de Status Codes
              </h5>
              <small className="text-muted">
                Proporção de códigos HTTP (2xx vs 3xx vs 4xx vs 5xx)
              </small>
            </Card.Header>
            <Card.Body style={{ padding: "20px" }}>
              {stats?.status_distribution &&
              stats.status_distribution.length > 0 ? (
                <div
                  className="doughnut-chart-container"
                  style={{ height: "400px", position: "relative" }}
                >
                  <Doughnut
                    data={getStatusDistributionChartData()}
                    options={statusDistributionChartOptions}
                    height={400}
                    width={null}
                  />
                </div>
              ) : (
                <div
                  style={{
                    height: "400px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <p className="text-muted">
                    Nenhum dado de status codes disponível
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Card com estatísticas detalhadas de status codes */}
        <Col md={6} className="mb-4">
          <Card className="metric-card h-100">
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-info-circle text-primary me-2"></i>
                Detalhes dos Status Codes
              </h5>
              <small className="text-muted">
                Estatísticas detalhadas por categoria
              </small>
            </Card.Header>
            <Card.Body>
              {stats?.status_distribution &&
              stats.status_distribution.length > 0 ? (
                <div>
                  {stats.status_distribution.map((status, index) => {
                    const total = stats.status_distribution.reduce(
                      (sum, item) => sum + item.count,
                      0
                    );
                    const percentage = ((status.count / total) * 100).toFixed(
                      1
                    );

                    const getStatusColor = statusGroup => {
                      switch (statusGroup) {
                        case "2xx":
                          return "success";
                        case "3xx":
                          return "info";
                        case "4xx":
                          return "warning";
                        case "5xx":
                          return "danger";
                        default:
                          return "secondary";
                      }
                    };

                    const getStatusIcon = statusGroup => {
                      switch (statusGroup) {
                        case "2xx":
                          return "fas fa-check-circle";
                        case "3xx":
                          return "fas fa-info-circle";
                        case "4xx":
                          return "fas fa-exclamation-triangle";
                        case "5xx":
                          return "fas fa-times-circle";
                        default:
                          return "fas fa-question-circle";
                      }
                    };

                    return (
                      <div
                        key={index}
                        className="d-flex justify-content-between align-items-center mb-3"
                      >
                        <div className="d-flex align-items-center">
                          <i
                            className={`${getStatusIcon(
                              status.status_group
                            )} text-${getStatusColor(
                              status.status_group
                            )} me-2`}
                          ></i>
                          <div>
                            <strong>{status.status_group}</strong>
                            <br />
                            <small className="text-muted">
                              {status.count.toLocaleString()} requisições
                            </small>
                          </div>
                        </div>
                        <div className="text-end">
                          <Badge bg={getStatusColor(status.status_group)}>
                            {percentage}%
                          </Badge>
                        </div>
                      </div>
                    );
                  })}

                  <hr />

                  <div className="text-center">
                    <h6 className="text-muted">Total de Requisições</h6>
                    <h3 className="text-primary">
                      {stats.status_distribution
                        .reduce((sum, item) => sum + item.count, 0)
                        .toLocaleString()}
                    </h3>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <i
                    className="fas fa-chart-pie text-muted"
                    style={{ fontSize: "3rem" }}
                  ></i>
                  <p className="text-muted mt-3">
                    Nenhum dado de status codes disponível
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Gráfico de Endpoints Lentos */}
      <Row className="mt-4" id="logs-slow-endpoints">
        <Col md={12} className="mb-4">
          <Card className="metric-card">
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-tachometer-alt text-danger me-2"></i>
                Top 10 Endpoints Lentos
              </h5>
              <small className="text-muted">
                Endpoints com maior tempo médio de resposta
                (APILogModel.endpoint vs response_time)
              </small>
            </Card.Header>
            <Card.Body style={{ padding: "20px" }}>
              {stats?.slow_endpoints && stats.slow_endpoints.length > 0 ? (
                <div
                  className="slow-endpoints-chart-container"
                  style={{ height: "450px", position: "relative" }}
                >
                  <Bar
                    data={getSlowEndpointsChartData()}
                    options={slowEndpointsChartOptions}
                    height={450}
                    width={null}
                  />
                </div>
              ) : (
                <div
                  style={{
                    height: "450px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <p className="text-muted">
                    Nenhum dado de endpoints lentos disponível
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabela Detalhada de Endpoints Lentos */}
      <Row className="mt-4">
        <Col md={12} className="mb-4">
          <Card className="metric-card">
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-table text-info me-2"></i>
                Detalhes dos Endpoints Lentos
              </h5>
              <small className="text-muted">
                Análise detalhada dos endpoints com pior performance
              </small>
            </Card.Header>
            <Card.Body>
              {stats?.slow_endpoints && stats.slow_endpoints.length > 0 ? (
                <div className="table-responsive">
                  <Table
                    striped
                    bordered
                    hover
                    size="sm"
                    style={{ fontSize: "0.875rem" }}
                  >
                    <thead className="table-dark">
                      <tr style={{ height: "40px" }}>
                        <th style={{ padding: "8px", verticalAlign: "middle" }}>
                          #
                        </th>
                        <th style={{ padding: "8px", verticalAlign: "middle" }}>
                          Endpoint
                        </th>
                        <th style={{ padding: "8px", verticalAlign: "middle" }}>
                          Tempo Médio (ms)
                        </th>
                        <th style={{ padding: "8px", verticalAlign: "middle" }}>
                          Total de Requisições
                        </th>
                        <th style={{ padding: "8px", verticalAlign: "middle" }}>
                          Performance
                        </th>
                        <th style={{ padding: "8px", verticalAlign: "middle" }}>
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.slow_endpoints
                        .slice(0, 10)
                        .map((endpoint, index) => {
                          const getPerformanceColor = avgTime => {
                            if (avgTime > 1000) return "danger";
                            if (avgTime > 500) return "warning";
                            if (avgTime > 200) return "info";
                            return "success";
                          };

                          const getPerformanceLabel = avgTime => {
                            if (avgTime > 1000) return "Muito Lento";
                            if (avgTime > 500) return "Lento";
                            if (avgTime > 200) return "Moderado";
                            return "Bom";
                          };

                          const getPerformanceIcon = avgTime => {
                            if (avgTime > 1000)
                              return "fas fa-exclamation-triangle";
                            if (avgTime > 500) return "fas fa-clock";
                            if (avgTime > 200) return "fas fa-info-circle";
                            return "fas fa-check-circle";
                          };

                          return (
                            <tr key={index} style={{ height: "35px" }}>
                              <td
                                style={{
                                  padding: "6px 8px",
                                  verticalAlign: "middle"
                                }}
                              >
                                <Badge
                                  bg="secondary"
                                  style={{ fontSize: "0.75rem" }}
                                >
                                  {index + 1}
                                </Badge>
                              </td>
                              <td
                                style={{
                                  padding: "6px 8px",
                                  verticalAlign: "middle"
                                }}
                              >
                                <code
                                  className="text-primary"
                                  style={{ fontSize: "0.8rem" }}
                                >
                                  {endpoint.endpoint}
                                </code>
                              </td>
                              <td
                                style={{
                                  padding: "6px 8px",
                                  verticalAlign: "middle"
                                }}
                              >
                                <strong
                                  className={`text-${getPerformanceColor(
                                    endpoint.avg_response_time
                                  )}`}
                                  style={{ fontSize: "0.85rem" }}
                                >
                                  {endpoint.avg_response_time.toFixed(2)} ms
                                </strong>
                              </td>
                              <td
                                style={{
                                  padding: "6px 8px",
                                  verticalAlign: "middle"
                                }}
                              >
                                <span
                                  className="text-muted"
                                  style={{ fontSize: "0.8rem" }}
                                >
                                  {endpoint.request_count.toLocaleString()} req
                                </span>
                              </td>
                              <td
                                style={{
                                  padding: "6px 8px",
                                  verticalAlign: "middle"
                                }}
                              >
                                <div className="d-flex align-items-center">
                                  <i
                                    className={`${getPerformanceIcon(
                                      endpoint.avg_response_time
                                    )} text-${getPerformanceColor(
                                      endpoint.avg_response_time
                                    )} me-1`}
                                    style={{ fontSize: "0.8rem" }}
                                  ></i>
                                  <Badge
                                    bg={getPerformanceColor(
                                      endpoint.avg_response_time
                                    )}
                                    style={{ fontSize: "0.7rem" }}
                                  >
                                    {getPerformanceLabel(
                                      endpoint.avg_response_time
                                    )}
                                  </Badge>
                                </div>
                              </td>
                              <td
                                style={{
                                  padding: "6px 8px",
                                  verticalAlign: "middle"
                                }}
                              >
                                <div className="d-flex align-items-center">
                                  <i
                                    className="fas fa-chart-line text-info me-1"
                                    style={{ fontSize: "0.75rem" }}
                                  ></i>
                                  <small
                                    className="text-muted"
                                    style={{ fontSize: "0.75rem" }}
                                  >
                                    {(
                                      (endpoint.avg_response_time / 1000) *
                                      100
                                    ).toFixed(1)}
                                    % do tempo total
                                  </small>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <i
                    className="fas fa-tachometer-alt text-muted"
                    style={{ fontSize: "3rem" }}
                  ></i>
                  <p className="text-muted mt-3">
                    Nenhum endpoint lento encontrado
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Sessões Ativas */}
      <Row className="mt-4" id="logs-section-sessions">
        <Col md={12} className="mb-4">
          <Card className="metric-card">
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-users text-success me-2"></i>
                Sessões Ativas
              </h5>
              <small className="text-muted">
                Monitoramento de sessões em andamento
              </small>
            </Card.Header>
            <Card.Body style={{ padding: "20px" }}>
              {/* Card com número total de sessões */}
              <Row className="mb-4">
                <Col md={4}>
                  <Card
                    className="text-center"
                    style={{
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #dee2e6"
                    }}
                  >
                    <Card.Body>
                      <div className="mb-2">
                        <i
                          className="fas fa-user-clock text-success"
                          style={{ fontSize: "2rem" }}
                        ></i>
                      </div>
                      <h3 className="text-success mb-1">
                        {stats?.active_sessions?.count ||
                          stats?.overview?.active_sessions ||
                          "0"}
                      </h3>
                      <p className="text-muted mb-0">Sessões Ativas</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={8}>
                  <div className="d-flex align-items-center h-100">
                    <div>
                      <h6 className="text-muted mb-1">
                        Informações das Sessões
                      </h6>
                      <p className="text-muted mb-0">
                        {stats?.active_sessions?.details?.length || 0} sessões
                        com detalhes disponíveis
                      </p>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Tabela com detalhes das sessões */}
              {stats?.active_sessions?.details &&
              stats.active_sessions.details.length > 0 ? (
                <div>
                  <h6 className="mb-3">Detalhes das Sessões Ativas</h6>
                  <div className="table-responsive">
                    <Table striped bordered hover size="sm">
                      <thead className="table-dark">
                        <tr>
                          <th>Usuário</th>
                          <th>IP</th>
                          <th>Localização</th>
                          <th>Dispositivo</th>
                          <th>Início da Sessão</th>
                          <th>Tempo Ativo</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.active_sessions.details.map((session, index) => (
                          <tr key={index}>
                            <td>
                              <div>
                                <strong>
                                  {session.username ||
                                    `Usuário ${session.user_id}`}
                                </strong>
                                <br />
                                <small className="text-muted">
                                  ID: {session.user_id}
                                </small>
                              </div>
                            </td>
                            <td>
                              <code className="text-primary">
                                {session.ip_address || "N/A"}
                              </code>
                            </td>
                            <td>
                              <div>
                                <i className="fas fa-map-marker-alt text-danger me-1"></i>
                                {session.city || "N/A"}
                                {session.country && (
                                  <Badge bg="secondary" className="ms-1">
                                    {session.country}
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td>
                              <div>
                                <i className="fas fa-mobile-alt text-info me-1"></i>
                                {session.device_type || "Desktop"}
                              </div>
                            </td>
                            <td>
                              <div>
                                <i className="fas fa-clock text-warning me-1"></i>
                                {new Date(session.session_start).toLocaleString(
                                  "pt-BR"
                                )}
                              </div>
                            </td>
                            <td>
                              <Badge bg="success">
                                <i className="fas fa-play me-1"></i>
                                {getSessionDuration(session.session_start)}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg="success">
                                <i className="fas fa-circle me-1"></i>
                                Ativa
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <i
                    className="fas fa-users text-muted"
                    style={{ fontSize: "3rem" }}
                  ></i>
                  <p className="text-muted mt-3">
                    Nenhuma sessão ativa encontrada
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <UserTimelineModal
        show={!!timelineUser}
        onHide={() => setTimelineUser(null)}
        userId={timelineUser?.id}
        username={timelineUser?.username}
        token={token}
      />

      {/* Espaçamento extra no final da página */}
      <div style={{ height: "100px" }}></div>
    </div>
  );
}

const mapStateToProps = state => ({
  token: state.auth.accessToken
});

export default connect(mapStateToProps)(LogsDashboard);
